import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Menu, Copy } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { canUserSendCopilotMessage } from '@/services/subscriptionService';
import TemplatePicker from './TemplatePicker';
import EnhancedChatInput, { ChatMode } from './EnhancedChatInput';
import { useProductCopilotChatSession } from '@/hooks/useProductCopilotChatSession';
import ChatMessage from './ChatMessage';

export type CopilotMode = 'free' | 'enhance' | 'template';

interface ChatSessionProps {
  sessionId?: string;
  initialPrompt?: string;
  onToggleSidebar?: () => void;
  sidebarCollapsed?: boolean;
  remainingCredits?: number;
  mode: CopilotMode;
  onModeChange: (mode: CopilotMode) => void;
}

interface Template {
  id: string;
  title: string;
  description: string;
  content: string;
  category: string;
  type: 'saved' | 'bookmark' | 'popular' | 'product' | 'collection';
  custom_title?: string;
  notes?: string;
}

const ChatSession: React.FC<ChatSessionProps> = ({
  sessionId,
  initialPrompt,
  onToggleSidebar,
  sidebarCollapsed = false,
  remainingCredits,
  mode,
  onModeChange
}) => {
  const { user } = useAuth();
  const [input, setInput] = useState('');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [usageInfo, setUsageInfo] = useState<{
    canSend: boolean;
    usedCount: number;
    limit: number;
    percentUsed: number;
  } | null>(null);
  const [hasResetForNewChat, setHasResetForNewChat] = useState(false);

  const {
    messages,
    isLoading,
    sessionId: currentSessionId,
    promptContent,
    sendMessage,
    addEnhancedMessage,
    resetChatState
  } = useProductCopilotChatSession(sessionId || null, selectedTemplate?.id || null, user?.id || null);

  const suggestions = [
    "Help me write a professional email",
    "Create a marketing strategy", 
    "Write technical documentation",
    "Generate social media content",
    "Compose a business proposal",
    "Draft meeting notes template"
  ];

  useEffect(() => {
    if (user) {
      checkUsageLimits();
    }
  }, [user]);

  useEffect(() => {
    if (initialPrompt && initialPrompt !== input) {
      setInput(initialPrompt);
    }
  }, [initialPrompt]);

  // Reset everything when sessionId is undefined (new chat) - but only once
  useEffect(() => {
    if (!sessionId && !hasResetForNewChat) {
      console.log('New chat detected - resetting all state');
      resetChatState();
      setInput('');
      setSelectedTemplate(null);
      setShowTemplatePicker(false);
      setHasResetForNewChat(true);
      // Only reset mode to free if we haven't already reset for this new chat
      if (mode !== 'free') {
        onModeChange('free');
      }
    } else if (sessionId) {
      // Reset the flag when we have a session ID
      setHasResetForNewChat(false);
    }
  }, [sessionId, resetChatState, mode, onModeChange, hasResetForNewChat]);

  const checkUsageLimits = async () => {
    if (!user) return;
    
    try {
      const usage = await canUserSendCopilotMessage(user.id);
      setUsageInfo(usage);
    } catch (error) {
      console.error('Error checking usage limits:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || !user) return;

    if (usageInfo && !usageInfo.canSend) {
      return;
    }

    const message = input.trim();
    setInput('');
    
    try {
      await sendMessage(message, mode, selectedTemplate?.content);
      await checkUsageLimits();
    } catch (error) {
      console.error('Error sending message:', error);
      setInput(message);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
  };

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
    setInput('');
    onModeChange('template');
    setShowTemplatePicker(false);
  };

  const handleClearTemplate = () => {
    setSelectedTemplate(null);
    setInput('');
    onModeChange('free');
  };

  const handleInputChange = (value: string) => {
    setInput(value);
  };

  const getModeDescription = () => {
    switch (mode) {
      case 'enhance':
        return 'Transform your basic ideas into powerful, detailed prompts';
      case 'template':
        return selectedTemplate 
          ? `Using "${selectedTemplate.title}" template for guided conversations`
          : 'Select a template for guided conversations';
      default:
        return 'Natural conversation with AI assistant';
    }
  };

  const getWelcomeTitle = () => {
    switch (mode) {
      case 'enhance':
        return 'Transform your ideas into powerful prompts';
      case 'template':
        return selectedTemplate 
          ? `Chat with AI using "${selectedTemplate.title}" template`
          : 'Select a template to get started';
      default:
        return 'How can I help you today?';
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Simple Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleSidebar}
            className="md:hidden"
          >
            <Menu className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Product Copilot</h1>
          </div>
        </div>
        
        {/* Usage Info */}
        {usageInfo && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>{usageInfo.usedCount} / {usageInfo.limit} used</span>
            <div className="w-16 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${usageInfo.percentUsed}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Conversation Area */}
      <div className="flex-1 flex flex-col min-h-0">
        <ScrollArea className="flex-1 px-4 chat-scroll-area">
          <div className="max-w-4xl mx-auto py-6">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full min-h-[60vh]">
                <div className="text-center space-y-6">
                  <div className="text-gray-500 dark:text-gray-400">
                    <Sparkles className="h-16 w-16 mx-auto mb-6 opacity-50" />
                    <h2 className="text-2xl font-semibold mb-4">
                      {getWelcomeTitle()}
                    </h2>
                    <p className="text-lg mb-8">
                      {getModeDescription()}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary hover:bg-accent transition-colors"
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {suggestion}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {messages.map((message) => (
                  <div key={message.id}>
                    <div className="flex items-start gap-3">
                      <ChatMessage message={message} />
                    </div>
                    {message.role === 'assistant' && (
                      <div className="flex gap-2 mt-2 ml-12">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(message.content)}
                          className="flex items-center gap-1"
                        >
                          <Copy className="h-3 w-3" />
                          Copy
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Loading Indicator */}
                {isLoading && (
                  <div className="flex items-center gap-3 text-gray-500">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <span className="text-sm">AI is thinking...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Enhanced Chat Input */}
        <EnhancedChatInput
          input={input}
          setInput={handleInputChange}
          onSend={handleSendMessage}
          isGenerating={isLoading}
          onOpenTemplatePicker={() => setShowTemplatePicker(true)}
          selectedTemplate={selectedTemplate}
          onClearTemplate={handleClearTemplate}
          mode={mode as ChatMode}
          onModeChange={(newMode) => onModeChange(newMode as CopilotMode)}
        />
      </div>

      {/* Template Picker Modal */}
      <TemplatePicker
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelectTemplate={handleTemplateSelect}
      />
    </div>
  );
};

export default ChatSession;
